import React from 'react';
import { Clapperboard, Plus, Minus, Check, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const REEL_OPTIONS = [
  { value: 'simple', label: 'Simple', price: 200000, description: 'Basic editing, clean cuts' },
  { value: 'subtitles', label: 'With subtitles', price: 300000, description: 'Editing + animated subtitles' },
  { value: 'motion_design', label: 'Motion Design', price: 500000, description: 'Animated graphics, visual effects' },
  { value: 'premium', label: 'Premium', price: 800000, description: 'Motion design + sound design + advanced effects' }
];

export function ReelsEditingSection({ reels, onChange, formatIDR }) {
  const addReel = () => {
    const newReel = {
      id: Date.now(),
      type: 'simple',
      coverDesign: false
    };
    onChange([...reels, newReel]);
  };

  const removeReel = (id) => {
    onChange(reels.filter(r => r.id !== id));
  };

  const updateReel = (id, updates) => {
    onChange(reels.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const calculateTotalPrice = () => {
    return reels.reduce((total, reel) => {
      const optionPrice = REEL_OPTIONS.find(o => o.value === reel.type)?.price || 0;
      const coverPrice = reel.coverDesign ? 150000 : 0;
      return total + optionPrice + coverPrice;
    }, 0);
  };

  return (
    <div 
      className="p-6 border border-border hover:border-primary/50 transition-colors"
      data-testid="reels-editing-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${reels.length > 0 ? 'bg-primary/20' : 'bg-muted'}`}>
            <Clapperboard className={`w-6 h-6 ${reels.length > 0 ? 'text-primary' : 'text-foreground-muted'}`} />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Reels / Shorts Editing</h3>
            <p className="text-sm text-foreground-muted">Up to 60 seconds per reel</p>
          </div>
        </div>
        
        {reels.length > 0 && (
          <span className="text-primary font-semibold" data-testid="reels-total-price">
            {formatIDR(calculateTotalPrice())}
          </span>
        )}
      </div>

      {/* Reels List */}
      <div className="space-y-4">
        <AnimatePresence>
          {reels.map((reel, index) => (
            <motion.div
              key={reel.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="p-4 bg-muted/30 border border-border"
              data-testid={`reel-item-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-heading font-semibold text-primary">
                  Reel #{index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeReel(reel.id)}
                  className="p-2 text-foreground-muted hover:text-red-500 transition-colors"
                  data-testid={`remove-reel-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Editing type */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground-muted mb-2 block uppercase tracking-wider">
                  Editing type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {REEL_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateReel(reel.id, { type: option.value })}
                      className={`p-3 border-2 transition-all text-left ${
                        reel.type === option.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`reel-${index}-type-${option.value}`}
                    >
                      <span className="block font-semibold text-sm">{option.label}</span>
                      <span className="text-xs text-foreground-muted block mb-1">{option.description}</span>
                      <span className="text-xs text-primary font-bold">{formatIDR(option.price)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cover Design option */}
              <label 
                className="flex items-center justify-between p-3 border border-border hover:border-primary/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                    reel.coverDesign ? 'bg-primary border-primary' : 'border-border'
                  }`}>
                    {reel.coverDesign && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <div>
                    <span className="font-medium text-sm">Cover Design</span>
                    <p className="text-xs text-foreground-muted">Custom thumbnail for your reel</p>
                  </div>
                </div>
                <span className="text-primary font-semibold text-sm">+{formatIDR(150000)}</span>
                <input
                  type="checkbox"
                  checked={reel.coverDesign}
                  onChange={(e) => updateReel(reel.id, { coverDesign: e.target.checked })}
                  className="sr-only"
                  data-testid={`reel-${index}-cover-checkbox`}
                />
              </label>

              {/* Reel price */}
              <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
                <span className="text-xs text-foreground-muted">Subtotal reel #{index + 1}</span>
                <span className="font-semibold text-primary">
                  {formatIDR(
                    (REEL_OPTIONS.find(o => o.value === reel.type)?.price || 0) +
                    (reel.coverDesign ? 150000 : 0)
                  )}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Add Reel Button */}
      <button
        type="button"
        onClick={addReel}
        className="w-full mt-4 p-4 border-2 border-dashed border-border hover:border-primary text-foreground-muted hover:text-primary transition-colors flex items-center justify-center gap-2"
        data-testid="add-reel-btn"
      >
        <Plus className="w-5 h-5" />
        <span className="font-semibold">Add a reel</span>
      </button>

      {/* Info */}
      {reels.length === 0 && (
        <p className="text-center text-sm text-foreground-muted mt-4">
          Click "Add a reel" to configure your edits
        </p>
      )}
    </div>
  );
}
